// Shared Word-Home-ribbon UI. Renders the Clipboard / Font / Paragraph /
// Styles groups against whatever Quill instance it's pointed at via
// `getEditor()`. Every existing hand-built ribbon (Workspace, Mail Merge)
// keeps its own tab strip / window chrome / surface-specific groups
// (Mailings, Insert, Review, etc.) — this component is the one shared
// implementation of the parts of the ribbon that are the same everywhere:
// text formatting. Surfaces compose it into their own "Home" tab instead of
// hand-rolling font/size/bold/paragraph controls a fourth and fifth time.
//
// Deliberately dependency-light: no external UI kit, matches the existing
// codebase's inline-style RBtn/RGroup convention so it drops into any of
// the three surfaces without a new visual language.
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { RIcon } from "../RibbonIcons";
import {
  FONT_LIST, THEME_FONTS, DOCUMENT_FONTS, SIZE_LIST, DEFAULT_SIZE,
  CASE_MODES, UNDERLINE_STYLES, TEXT_EFFECTS, STYLE_GALLERY,
  BULLET_STYLES, NUMBER_FORMATS, THEME_COLOR_GRID, STANDARD_COLORS,
  changeCase, sortSelectionLines, applyStyleGallery, insertHorizontalLine,
} from "./wordFormats";
import { cleanPastedHtml, escapeHtml, insertCleanedHtml } from "./pasteClean";

const HIGHLIGHT_COLORS = ["#FFFF00","#00FF00","#00FFFF","#FF00FF","#0000FF","#FF0000","#000080","#008080","#008000","#800080","#800000","#808000","#808080","#000000",""];
const LINE_SPACINGS = [{v:"1",l:"1.0"},{v:"1.15",l:"1.15"},{v:"1.5",l:"1.5"},{v:"2",l:"2.0"},{v:"2.5",l:"2.5"},{v:"3",l:"3.0"}];

// Closes the flyout on any mousedown that lands outside ALL of the given
// refs. Flyout panels are portaled to document.body (see `useFlyoutPosition`
// below), so a single ref no longer covers both the trigger AND the panel —
// callers pass one ref per DOM subtree that should count as "inside".
function useOutsideClose(open, setOpen, refs) {
  useEffect(() => {
    if (!open) return;
    const list = refs || [];
    const h = (e) => {
      const inside = list.some(r => r && r.current && r.current.contains(e.target));
      if (!inside) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open, setOpen]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Computes fixed-viewport coordinates for a flyout panel anchored under
// `anchorRef`'s element, re-measuring on open/scroll/resize. Portaling the
// panel to document.body + position:"fixed" means it's never subject to an
// ancestor's `overflow: hidden`/`auto` clipping it — that was the root cause
// of ribbon dropdowns (Change Case, Font Color, Highlight, Borders, Shading,
// Text Effects, Underline styles, Paste Special, Line Spacing, and the
// Font Family / Font Size comboboxes) rendering clipped inside the ribbon
// bar in Sequences, Mail Merge, and Workspace alike.
//
// The panels' own z-index has to clear 99999, not just other in-ribbon
// popups' z-indexes: the Workspace and Mail Merge popup-window routes each
// wrap their whole app in their own position:"fixed",zIndex:99999 full-
// screen layer (App.js). Everything else in those files lives INSIDE that
// layer, so any z-index is compared only against ribbon-local siblings and
// a modest value is enough — but these panels are portaled OUTSIDE it,
// straight onto document.body, so they're stacked as a direct sibling of
// that 99999 layer and get compared against it directly. At the old
// zIndex:9999 they lost that comparison and rendered fully invisible
// (DOM present, correctly positioned, but painted over by the opaque app
// shell) — this was the "font/size dropdowns don't drop down" bug. 100002
// clears it with room to spare, matching the 100000/100001 convention
// RibbonPrimitives.js's own document.body portal already uses for the same
// reason.
function useFlyoutPosition(open, anchorRef, panelWidth, align) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!open) { setPos(null); return; }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 8;
      let left = align === "right" ? r.right - panelWidth : r.left;
      if (left + panelWidth > window.innerWidth - margin) left = window.innerWidth - margin - panelWidth;
      if (left < margin) left = margin;
      let top = r.bottom + 3;
      if (top > window.innerHeight - margin) top = Math.max(margin, r.top - 3);
      setPos({ top, left });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchorRef, panelWidth, align]);
  return pos;
}

function RGroup({ label, children, style }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "3px 8px", borderRight: "1px solid #e2e6f0", minWidth: 0, ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>{children}</div>
      <span style={{ fontSize: 9.5, color: "#8890B0", marginTop: 2, whiteSpace: "nowrap" }}>{label}</span>
    </div>
  );
}

// "Light black" throughout this ribbon — LABEL_COLOR (#514F4D, a medium
// gray) replaces what used to be #201F1E (near-black) on every icon/label,
// matching the same lighten-from-bold-black pass already applied to Mail
// Merge's own ribbon groups. Left untouched: format-state indicator glyphs
// that are supposed to show an actual color (the Font Color "A" underline,
// the Highlight "A" background swatch) — those intentionally reflect
// whatever color is actually applied, not the ribbon's own text color.
const LABEL_COLOR = "#514F4D";

function RBtn({ icon, label, title, active, onClick, disabled, small }) {
  return (
    <button type="button" title={title || label} disabled={disabled}
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 1, background: active ? "#DEECF9" : "transparent", border: "1px solid " + (active ? "#0078D4" : "transparent"),
        borderRadius: 8, padding: small ? "3px 4px" : "4px 6px", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1, color: LABEL_COLOR, minWidth: small ? 22 : 30,
      }}
      onMouseEnter={e => { if (!disabled && !active) e.currentTarget.style.background = "#F0F4FF"; }}
      onMouseLeave={e => { if (!disabled && !active) e.currentTarget.style.background = "transparent"; }}>
      {typeof icon === "string" ? <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>{icon}</span> : icon}
      {label && !small && <span style={{ fontSize: 8.5, color: LABEL_COLOR }}>{label}</span>}
    </button>
  );
}

function Dropdown({ trigger, open, setOpen, children, width = 200, align = "left" }) {
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  useOutsideClose(open, setOpen, [anchorRef, panelRef]);
  const pos = useFlyoutPosition(open, anchorRef, width, align);
  return (
    <div ref={anchorRef} style={{ position: "relative" }}>
      {trigger}
      {open && pos && createPortal(
        <div ref={panelRef} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 100002, background: "#fff", border: "1px solid #d1cec9", borderRadius: 10, boxShadow: "0 8px 24px rgba(16,24,40,.16)", padding: 4, width, maxHeight: 320, overflowY: "auto" }}>
          {children}
        </div>,
        document.body
      )}
    </div>
  );
}

function MenuItem({ children, onClick, style }) {
  return (
    <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClick}
      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "none", border: "none", padding: "6px 8px", fontSize: 12, color: LABEL_COLOR, borderRadius: 6, cursor: "pointer", ...style }}
      onMouseEnter={e => e.currentTarget.style.background = "#F0F4FF"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
      {children}
    </button>
  );
}

// ---- Font Family combo: type-to-search, Theme Fonts pinned at top -------
function FontFamilyBox({ quill, value, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  useOutsideClose(open, setOpen, [anchorRef, panelRef]);
  const pos = useFlyoutPosition(open, anchorRef, 200, "left");
  const current = FONT_LIST.find(f => f.value === value);
  const filtered = (list) => list.filter(f => !q || f.label.toLowerCase().includes(q.toLowerCase()));
  return (
    <div ref={anchorRef} style={{ position: "relative", width: 118 }}>
      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => { setOpen(o => !o); setQ(""); }}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", background: "#fff", border: "1px solid #d1cec9", borderRadius: 8, padding: "3px 6px", fontSize: 12, cursor: "pointer", color: LABEL_COLOR }}
        title="Font — click to search">
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: current?.css }}>{current?.label || "Calibri"}</span>
        <span style={{ fontSize: 9, color: "#8890B0" }}>▾</span>
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 100002, background: "#fff", border: "1px solid #d1cec9", borderRadius: 10, boxShadow: "0 8px 24px rgba(16,24,40,.16)", width: 200 }}>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search fonts..."
            style={{ width: "100%", boxSizing: "border-box", border: "none", borderBottom: "1px solid #e2e6f0", padding: "7px 9px", fontSize: 12, outline: "none" }} />
          <div style={{ maxHeight: 260, overflowY: "auto", padding: 4 }}>
            {filtered(THEME_FONTS).length > 0 && <div style={{ fontSize: 9.5, color: "#8890B0", padding: "4px 6px", textTransform: "uppercase" }}>Theme Fonts</div>}
            {filtered(THEME_FONTS).map(f => (
              <MenuItem key={f.value} onClick={() => { onPick(f.value); setOpen(false); }} style={{ fontFamily: f.css }}>{f.label}</MenuItem>
            ))}
            {filtered(DOCUMENT_FONTS).length > 0 && <div style={{ fontSize: 9.5, color: "#8890B0", padding: "4px 6px", textTransform: "uppercase" }}>All Fonts</div>}
            {filtered(DOCUMENT_FONTS).map(f => (
              <MenuItem key={f.value} onClick={() => { onPick(f.value); setOpen(false); }} style={{ fontFamily: f.css }}>{f.label}</MenuItem>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ---- Font Size combo: pick a preset OR type any custom number -----------
function FontSizeBox({ value, onPick }) {
  const num = (value || DEFAULT_SIZE).replace(/[^\d.]/g, "") || "11";
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(num);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  useOutsideClose(open, setOpen, [anchorRef, panelRef]);
  const pos = useFlyoutPosition(open, anchorRef, 60, "left");
  useEffect(() => setText(num), [num]);
  const commit = () => {
    const n = parseFloat(text);
    if (!isNaN(n) && n > 0) onPick(n + "pt");
    else setText(num);
  };
  return (
    <div ref={anchorRef} style={{ position: "relative", width: 42 }}>
      <div style={{ display: "flex", alignItems: "center", border: "1px solid #d1cec9", borderRadius: 8, overflow: "hidden" }}>
        <input value={text} onFocus={() => setOpen(true)}
          onChange={e => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={e => { if (e.key === "Enter") { commit(); setOpen(false); } }}
          style={{ width: 26, border: "none", padding: "3px 2px 3px 5px", fontSize: 12, outline: "none", color: LABEL_COLOR }} />
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setOpen(o => !o)} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 9, color: "#8890B0", padding: "0 3px" }}>▾</button>
      </div>
      {open && pos && createPortal(
        <div ref={panelRef} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 100002, background: "#fff", border: "1px solid #d1cec9", borderRadius: 10, boxShadow: "0 8px 24px rgba(16,24,40,.16)", width: 60, maxHeight: 240, overflowY: "auto" }}>
          {SIZE_LIST.map(s => (
            <MenuItem key={s.value} onClick={() => { onPick(s.value); setOpen(false); }}>{s.label}</MenuItem>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function ColorSwatchGrid({ colors, onPick, onClear, clearLabel = "Automatic" }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3, padding: 4 }}>
      {onClear && <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClear} title={clearLabel} style={{ gridColumn: "1/-1", fontSize: 11, textAlign: "left", background: "none", border: "none", padding: "3px 4px", cursor: "pointer", borderRadius: 6, color: LABEL_COLOR }} onMouseEnter={e => e.currentTarget.style.background = "#F0F4FF"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>{clearLabel}</button>}
      {colors.filter(Boolean).map(c => (
        <button key={c} type="button" title={c} onMouseDown={e => e.preventDefault()} onClick={() => onPick(c)}
          style={{ width: 18, height: 18, background: c, border: "1px solid #d1cec9", borderRadius: 3, cursor: "pointer", padding: 0 }} />
      ))}
    </div>
  );
}

// Word's real Font Color / Shading pickers: "Automatic"/"No Color" row,
// then a "Theme Colors" swatch grid (10 base colors x 6 tint/shade steps —
// THEME_COLOR_GRID, see wordFormats.js), then a "Standard Colors" row, then
// "More Colors..." — a real native <input type="color"> (an actual OS color
// picker, not a placeholder) so this is a genuinely complete picker, not a
// visual approximation missing its escape hatch.
function WordColorPicker({ onPick, onClear, clearLabel = "Automatic" }) {
  const nativeRef = useRef(null);
  return (
    <div style={{ padding: 6, width: 232 }}>
      {onClear && (
        <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClear}
          style={{ display: "block", width: "100%", textAlign: "left", fontSize: 12, color: LABEL_COLOR, background: "none", border: "none", padding: "4px 4px", cursor: "pointer", borderRadius: 6, marginBottom: 2 }}
          onMouseEnter={e => e.currentTarget.style.background = "#F0F4FF"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          {clearLabel}
        </button>
      )}
      <div style={{ fontSize: 9.5, color: LABEL_COLOR, textTransform: "uppercase", letterSpacing: ".3px", padding: "2px 4px" }}>Theme Colors</div>
      {/* Row-major, NOT column-major: each row is one tint/shade STEP across
          all 10 base colors (row 0 = the base swatches themselves), so
          each visual COLUMN stays one consistent hue top-to-bottom — matches
          Word's own layout. THEME_COLOR_GRID is stored as 10 arrays (one per
          base color) of 6 steps each, so this indexes [color][step] while
          iterating step-outer, color-inner to lay it out that way. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(10,1fr)", gap: 2, padding: "0 4px" }}>
        {THEME_COLOR_GRID[0].map((_, ri) => THEME_COLOR_GRID.map((col, ci) => (
          <button key={ci + "-" + ri} type="button" title={col[ri]} onMouseDown={e => e.preventDefault()} onClick={() => onPick(col[ri])}
            style={{ width: 16, height: 16, background: col[ri], border: "1px solid " + (col[ri] === "#FFFFFF" ? "#d1cec9" : "rgba(0,0,0,.15)"), borderRadius: 2, cursor: "pointer", padding: 0 }} />
        )))}
      </div>
      <div style={{ fontSize: 9.5, color: LABEL_COLOR, textTransform: "uppercase", letterSpacing: ".3px", padding: "6px 4px 2px" }}>Standard Colors</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(10,1fr)", gap: 2, padding: "0 4px" }}>
        {STANDARD_COLORS.map(hex => (
          <button key={hex} type="button" title={hex} onMouseDown={e => e.preventDefault()} onClick={() => onPick(hex)}
            style={{ width: 16, height: 16, background: hex, border: "1px solid rgba(0,0,0,.1)", borderRadius: 2, cursor: "pointer", padding: 0 }} />
        ))}
      </div>
      <div style={{ height: 1, background: "#e2e6f0", margin: "6px 2px" }} />
      <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => nativeRef.current?.click()}
        style={{ display: "block", width: "100%", textAlign: "left", fontSize: 12, color: LABEL_COLOR, background: "none", border: "none", padding: "4px 4px", cursor: "pointer", borderRadius: 6 }}
        onMouseEnter={e => e.currentTarget.style.background = "#F0F4FF"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
        More Colors…
      </button>
      <input ref={nativeRef} type="color" style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }}
        onChange={e => onPick(e.target.value)} />
    </div>
  );
}

// Central hook every host surface can use to know the live format at the
// cursor (for highlighting active buttons) without duplicating Quill event
// wiring in every file.
export function useQuillFormatState(getQuill) {
  const [fmt, setFmt] = useState({});
  const [hasSelection, setHasSelection] = useState(false);
  // No dependency array below is deliberate — `getQuill` is a fresh inline
  // closure on plenty of call sites, so depending on it wouldn't actually
  // stop this from re-running every render anyway. What used to make that
  // an infinite loop: `update()` called setFmt/setHasSelection with a
  // brand-new object every single time it ran, even when the cursor's
  // actual formatting hadn't changed — a new object reference always
  // triggers a re-render, which re-ran this effect, which called setState
  // again, forever ("Maximum update depth exceeded"). prevRef below is the
  // fix: skip the setState calls entirely when the format genuinely hasn't
  // changed, which breaks the loop while leaving every real format change
  // (selection move, text edit) reflected exactly as before.
  const prevRef = useRef({ key: "{}", hasSelection: false });
  useEffect(() => {
    const q = getQuill && getQuill();
    if (!q) return;
    const update = () => {
      const sel = q.getSelection();
      const nextHasSelection = !!sel;
      const nextFmt = sel ? q.getFormat(sel) : {};
      const nextKey = JSON.stringify(nextFmt);
      if (nextKey === prevRef.current.key && nextHasSelection === prevRef.current.hasSelection) return;
      prevRef.current = { key: nextKey, hasSelection: nextHasSelection };
      setHasSelection(nextHasSelection);
      setFmt(nextFmt);
    };
    update();
    q.on("selection-change", update);
    q.on("text-change", update);
    return () => { try { q.off("selection-change", update); q.off("text-change", update); } catch (e) {} };
  });
  return [fmt, hasSelection];
}

// The Home-tab ribbon content: Clipboard, Font, Paragraph, Styles groups.
// `getQuill` is a function returning the live Quill instance (so it always
// reflects the current ref, not a stale one captured at mount).
export default function WordRibbon({ getQuill, showClipboard = true, showStyles = true, formatMarksOn, onToggleFormatMarks, onFindReplace, addToast, docFilename = "document", onImported }) {
  const [fmt, hasSelection] = useQuillFormatState(getQuill);
  const [painting, setPainting] = useState(false);
  const paintedFormat = useRef(null);
  const [openMenu, setOpenMenu] = useState(null); // which dropdown id is open
  const [docBusy, setDocBusy] = useState(null); // null | "export" | "import"
  const importInputRef = useRef(null);
  const ribbonRootRef = useRef(null);
  const [showPalette, setShowPalette] = useState(false);

  const q = () => (getQuill ? getQuill() : null);
  const withSel = (fn) => { const quill = q(); if (!quill) return; const sel = quill.getSelection(true); fn(quill, sel); };

  // Export/Import as Word (.docx) — new, additive capability. Talks to the
  // new /api/documents/export-docx and /api/documents/import-docx routes
  // (server.js), which do not touch any existing send/campaign/sequence/
  // mail logic. Export sends the editor's current HTML and downloads the
  // returned .docx; import reads a chosen .docx, converts it server-side
  // (via mammoth — a scoped, honest conversion, not full Word fidelity),
  // and inserts the result at the cursor through the same paste-cleaning
  // pipeline (`cleanPastedHtml`/`insertCleanedHtml`) real pasted content
  // already goes through, so imported content behaves exactly like a paste
  // rather than a silent whole-document replacement.
  const exportDocx = useCallback(async () => {
    // See importDocxFile's comment below for why `getQuill` is a required
    // dependency here — same stale-closure fix, same silent-failure guard.
    const quill = q();
    if (!quill) { addToast?.("Editor isn't ready yet — click into the document, then try again.", "error"); return; }
    setDocBusy("export");
    try {
      const html = quill.root.innerHTML;
      const token = localStorage.getItem("sdr_token") || "";
      const r = await fetch("/api/documents/export-docx", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ html, filename: docFilename }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || "Export failed"); }
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = docFilename.replace(/[^a-zA-Z0-9 _.-]/g, "").trim() + ".docx";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      addToast?.("Downloaded as Word document", "success");
    } catch (e) { addToast?.("Couldn't export to Word: " + e.message, "error"); }
    setDocBusy(null);
  }, [docFilename, getQuill]); // eslint-disable-line react-hooks/exhaustive-deps

  // Print / Save as PDF — new, additive, zero-dependency (native browser
  // print API only, no client or server library added). Opens a clean,
  // print-styled copy of the editor's current HTML in a new window and
  // invokes the browser's own print dialog, where any browser's "Save as
  // PDF" destination produces a real, selectable-text PDF — deliberately
  // chosen over a canvas-rasterizing library (e.g. html2pdf.js) so output
  // quality matches an actual print rather than a screenshot, and so this
  // doesn't add a headless-Chromium dependency to the production backend
  // (which would risk the Railway deploy itself needing new system
  // libraries) or a large client bundle addition for the same result.
  const printAsPdf = useCallback(() => {
    const quill = q(); if (!quill) return;
    const html = quill.root.innerHTML;
    const win = window.open("", "_blank", "width=850,height=1100");
    if (!win) { addToast?.("Couldn't open print preview — check your browser's pop-up blocker", "error"); return; }
    win.document.open();
    win.document.write(
      "<!DOCTYPE html><html><head><title>" + escapeHtml(docFilename) + "</title><style>" +
      "body{font-family:Calibri,'Segoe UI',Arial,sans-serif;font-size:12pt;line-height:1.5;color:#1a1a1a;max-width:800px;margin:40px auto;padding:0 24px;}" +
      "img{max-width:100%;} table{border-collapse:collapse;} @media print{body{margin:0;padding:0 24px;}}" +
      "</style></head><body>" + html + "</body></html>"
    );
    win.document.close();
    win.focus();
    setTimeout(() => { try { if (!win.closed) win.print(); } catch (e) {} }, 350);
  }, [docFilename, getQuill]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save as HTML — same zero-dependency, client-only pattern as the PDF
  // button above. Closes the other confirmed "not built yet" gap
  // (FileMenu.js's Save As > "Save as HTML") with a document-scoped
  // version that actually works, instead of an account-level placeholder.
  const saveAsHtml = useCallback(() => {
    const quill = q(); if (!quill) return;
    const html = quill.root.innerHTML;
    const full = "<!DOCTYPE html><html><head><meta charset=\"utf-8\"><title>" + escapeHtml(docFilename) + "</title></head><body>" + html + "</body></html>";
    const blob = new Blob([full], { type: "text/html" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = docFilename.replace(/[^a-zA-Z0-9 _.-]/g, "").trim() + ".html";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
    addToast?.("Downloaded as HTML", "success");
  }, [docFilename, getQuill]); // eslint-disable-line react-hooks/exhaustive-deps

  const importDocxFile = useCallback(async (file) => {
    // `getQuill` is now a real dependency of this callback (see the fix
    // below) — root-caused a confirmed bug where Import (and Export/Print/
    // Save as HTML/Paste Special, same pattern) silently did nothing on
    // some surfaces: `q()` closes over whatever `getQuill` prop was current
    // when THIS useCallback was last (re)created. With `getQuill` missing
    // from the dependency array, a stable/undefined `onImported` prop
    // (Workspace and this Templates editor don't pass one) meant this
    // callback was created exactly once, on the very first render — before
    // the Quill ref callback had ever fired — and then frozen forever on
    // that first, still-null `quill` value, even after the editor mounted
    // for real a moment later. Mail Merge happened to dodge this by
    // accident: its inline `onImported={...}` arrow function is a fresh
    // reference every render, which incidentally forced this callback to
    // refresh too. Belt-and-suspenders: if `quill` is somehow still
    // unavailable, surface that instead of returning silently, so a picked
    // file can never again look like it vanished into nothing.
    const quill = q();
    if (!file) return;
    if (!quill) { addToast?.("Editor isn't ready yet — click into the template body, then try Import again.", "error"); return; }
    setDocBusy("import");
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(",").pop());
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const token = localStorage.getItem("sdr_token") || "";
      const r = await fetch("/api/documents/import-docx", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ base64 }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Import failed");
      insertCleanedHtml(quill, cleanPastedHtml(d.html, "keep"));
      addToast?.(d.warnings?.length ? "Imported — some advanced Word formatting may not be preserved" : "Word document imported", d.warnings?.length ? "info" : "success");
      // A .docx has no concept of an email "subject line" — this import
      // only ever fills the BODY, by design. Surfaces that also track a
      // separate subject field (Mail Merge) can pass `onImported` to fill
      // it in from the file name when the subject is still blank, so a
      // straight-to-Send attempt right after importing isn't blocked by a
      // subject the import could never have known to set.
      onImported?.(file.name.replace(/\.docx$/i, ""));
    } catch (e) { addToast?.("Couldn't import Word document: " + e.message, "error"); }
    setDocBusy(null);
  }, [onImported, getQuill]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (name) => withSel((quill, sel) => quill.format(name, !fmt[name], "user"));
  const setFormat = (name, val) => withSel((quill, sel) => quill.format(name, val, "user"));

  // mode: "keep" (Keep Source Formatting) | "merge" (Merge Formatting) |
  // "match" (Match Destination Formatting) | "text" (Keep Text Only).
  // Reads the system clipboard's real `text/html` representation (via the
  // async Clipboard API — there's no native paste event to intercept from
  // a menu-item click, unlike plain Ctrl+V, which RichTextEditor.js
  // handles separately) and runs it through the same pasteClean.js engine,
  // so these four menu items now actually differ from one another instead
  // of all falling back to identical plain-text inserts.
  const doPasteSpecial = useCallback(async (mode) => {
    const quill = q(); if (!quill) return;
    try {
      if (mode === "text") {
        const text = await navigator.clipboard.readText();
        if (!text) return;
        insertCleanedHtml(quill, escapeHtml(text).replace(/\n/g, "<br>"));
        return;
      }
      let html = null;
      if (navigator.clipboard && navigator.clipboard.read) {
        try {
          const items = await navigator.clipboard.read();
          for (const item of items) {
            if (item.types.includes("text/html")) { html = await (await item.getType("text/html")).text(); break; }
          }
        } catch (e) { /* HTML clipboard access denied/unsupported (older browser, insecure context, or user declined the permission prompt) — fall back to plain text below */ }
      }
      if (html) {
        insertCleanedHtml(quill, cleanPastedHtml(html, mode));
      } else {
        const text = await navigator.clipboard.readText();
        if (!text) return;
        insertCleanedHtml(quill, escapeHtml(text).replace(/\n/g, "<br>"));
      }
    } catch (e) { /* clipboard permission denied entirely — silently no-op, native Ctrl+V (auto-cleaned, see RichTextEditor.js) still works */ }
  }, [getQuill]); // eslint-disable-line react-hooks/exhaustive-deps

  const startFormatPainter = () => {
    const quill = q(); if (!quill) return;
    const sel = quill.getSelection();
    if (!sel) return;
    paintedFormat.current = quill.getFormat(sel);
    setPainting(true);
  };
  useEffect(() => {
    if (!painting) return;
    const quill = q(); if (!quill) return;
    const onSelChange = (range) => {
      if (!range || !paintedFormat.current) return;
      if (range.length > 0) {
        quill.removeFormat(range.index, range.length, "user");
        Object.entries(paintedFormat.current).forEach(([k, v]) => quill.formatText(range.index, range.length, k, v, "user"));
        setPainting(false);
        paintedFormat.current = null;
      }
    };
    quill.on("selection-change", onSelChange);
    return () => { try { quill.off("selection-change", onSelChange); } catch (e) {} };
  }, [painting]); // eslint-disable-line react-hooks/exhaustive-deps

  const underlineActive = !!fmt.underline;

  // Command palette — a real, working consumer of a small command list,
  // not scaffolding for its own sake. Every entry calls the exact same
  // function the corresponding ribbon button already calls (no duplicated
  // logic), so this is purely a second way to reach an action that already
  // exists, not new editing behavior. Ctrl+K opens it; only the currently
  // visible WordRibbon instance responds (guarded below), so this is safe
  // even though every editing surface renders this same component.
  const commands = useMemo(() => {
    const list = [
      { id: "bold", label: "Bold", keywords: "bold b", run: () => toggle("bold") },
      { id: "italic", label: "Italic", keywords: "italic i", run: () => toggle("italic") },
      { id: "underline", label: "Underline", keywords: "underline u", run: () => toggle("underline") },
      { id: "strike", label: "Strikethrough", keywords: "strike strikethrough", run: () => toggle("strike") },
      { id: "bullets", label: "Bulleted List", keywords: "bullets list unordered", run: () => setFormat("list", fmt.list === "bullet" ? false : "bullet") },
      { id: "numbering", label: "Numbered List", keywords: "numbering list ordered", run: () => setFormat("list", fmt.list === "ordered" ? false : "ordered") },
      { id: "align-left", label: "Align Left", keywords: "align left", run: () => setFormat("align", false) },
      { id: "align-center", label: "Center", keywords: "align center", run: () => setFormat("align", "center") },
      { id: "align-right", label: "Align Right", keywords: "align right", run: () => setFormat("align", "right") },
      { id: "align-justify", label: "Justify", keywords: "align justify", run: () => setFormat("align", "justify") },
      { id: "indent-more", label: "Increase Indent", keywords: "indent", run: () => withSel(quill => quill.format("indent", "+1", "user")) },
      { id: "indent-less", label: "Decrease Indent", keywords: "outdent indent", run: () => withSel(quill => quill.format("indent", "-1", "user")) },
      { id: "clear-format", label: "Clear Formatting", keywords: "clear remove format", run: () => withSel((quill, sel) => quill.removeFormat(sel.index, Math.max(sel.length, 0), "user")) },
      { id: "undo", label: "Undo", keywords: "undo", run: () => q()?.history?.undo?.() },
      { id: "redo", label: "Redo", keywords: "redo", run: () => q()?.history?.redo?.() },
      { id: "export-docx", label: "Export as Word (.docx)", keywords: "export download word docx save", run: exportDocx },
      { id: "import-docx", label: "Import Word Document (.docx)", keywords: "import open word docx upload", run: () => importInputRef.current?.click() },
      { id: "print-pdf", label: "Print / Save as PDF", keywords: "print pdf save export", run: printAsPdf },
      { id: "save-html", label: "Save as HTML", keywords: "save export html", run: saveAsHtml },
    ];
    if (onFindReplace) list.push({ id: "find-replace", label: "Find & Replace", keywords: "find replace search", run: onFindReplace });
    return list;
  }, [fmt.list, exportDocx, printAsPdf, saveAsHtml, onFindReplace]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => {
      if (!((e.ctrlKey || e.metaKey) && e.key === "k")) return;
      // Only the WordRibbon instance actually on screen should respond —
      // several surfaces mount this component, and while in practice only
      // one is visible at a time (each remounts its editor per tab/step
      // rather than keeping prior ones mounted), this guard makes that an
      // enforced invariant instead of an assumption.
      const el = ribbonRootRef.current;
      if (!el || el.offsetParent === null) return;
      e.preventDefault();
      setShowPalette(true);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  return (
    <div ref={ribbonRootRef} style={{ display: "flex", alignItems: "stretch" }}>
      {showClipboard && (
        <RGroup label="Clipboard">
          <RBtn small icon={<RIcon name="doc" size={14} />} title="Paste (Ctrl+V)" onClick={() => document.execCommand && document.execCommand("paste")} />
          <Dropdown width={210} open={openMenu === "pastespecial"} setOpen={o => setOpenMenu(o ? "pastespecial" : null)}
            trigger={<RBtn small icon="▾" title="Paste Special" onClick={() => setOpenMenu(m => m === "pastespecial" ? null : "pastespecial")} />}>
            <MenuItem onClick={() => { doPasteSpecial("keep"); setOpenMenu(null); }}>Keep Source Formatting</MenuItem>
            <MenuItem onClick={() => { doPasteSpecial("merge"); setOpenMenu(null); }}>Merge Formatting</MenuItem>
            <MenuItem onClick={() => { doPasteSpecial("match"); setOpenMenu(null); }}>Match Destination Formatting</MenuItem>
            <MenuItem onClick={() => { doPasteSpecial("text"); setOpenMenu(null); }}>Keep Text Only</MenuItem>
          </Dropdown>
          <RBtn small icon={<RIcon name="trash" size={13} />} title="Cut (Ctrl+X)" onClick={() => document.execCommand && document.execCommand("cut")} />
          <RBtn small icon={<RIcon name="doc" size={13} />} title="Copy (Ctrl+C)" onClick={() => document.execCommand && document.execCommand("copy")} />
          <RBtn small icon="🖌" title="Format Painter" active={painting} onClick={startFormatPainter} />
        </RGroup>
      )}

      <RGroup label="Font" style={{ minWidth: 260 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={{ display: "flex", gap: 3 }}>
            <FontFamilyBox value={fmt.font} onPick={v => setFormat("font", v)} />
            <FontSizeBox value={fmt.size} onPick={v => setFormat("size", v)} />
            <RBtn small icon="A˄" title="Increase Font Size" onClick={() => withSel((quill, sel) => {
              const cur = parseFloat((fmt.size || DEFAULT_SIZE)); quill.format("size", (cur + 2) + "pt", "user");
            })} />
            <RBtn small icon="A˅" title="Decrease Font Size" onClick={() => withSel((quill, sel) => {
              const cur = parseFloat((fmt.size || DEFAULT_SIZE)); quill.format("size", Math.max(1, cur - 2) + "pt", "user");
            })} />
            <Dropdown width={170} open={openMenu === "case"} setOpen={o => setOpenMenu(o ? "case" : null)}
              trigger={<RBtn small icon="Aa" title="Change Case" onClick={() => setOpenMenu(m => m === "case" ? null : "case")} />}>
              {CASE_MODES.map(c => <MenuItem key={c.id} onClick={() => { withSel(quill => changeCase(quill, c.id)); setOpenMenu(null); }}>{c.label}</MenuItem>)}
            </Dropdown>
            <RBtn small icon={<RIcon name="close" size={12} />} title="Clear Formatting" onClick={() => withSel((quill, sel) => quill.removeFormat(sel.index, Math.max(sel.length, 0), "user"))} />
          </div>
          <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <RBtn small icon="B" title="Bold (Ctrl+B)" active={!!fmt.bold} onClick={() => toggle("bold")} />
            <RBtn small icon={<span style={{ fontStyle: "italic", fontWeight: 700 }}>I</span>} title="Italic (Ctrl+I)" active={!!fmt.italic} onClick={() => toggle("italic")} />
            <Dropdown width={130} open={openMenu === "underline"} setOpen={o => setOpenMenu(o ? "underline" : null)}
              trigger={<RBtn small icon={<span style={{ textDecoration: "underline" }}>U</span>} title="Underline (Ctrl+U)" active={underlineActive} onClick={() => toggle("underline")} />}>
              {UNDERLINE_STYLES.map(s => (
                <MenuItem key={s.value} onClick={() => { withSel(quill => { quill.format("underline", true, "user"); quill.format("underlinestyle", s.value === "single" ? false : s.value, "user"); }); setOpenMenu(null); }}>
                  <span style={{ textDecoration: "underline", textDecorationStyle: s.value === "thick" || s.value === "single" ? "solid" : s.value }}>{s.label}</span>
                </MenuItem>
              ))}
            </Dropdown>
            <RBtn small icon={<span style={{ textDecoration: "line-through" }}>S</span>} title="Strikethrough" active={!!fmt.strike} onClick={() => toggle("strike")} />
            <RBtn small icon="x²" title="Superscript" active={fmt.script === "super"} onClick={() => setFormat("script", fmt.script === "super" ? false : "super")} />
            <RBtn small icon="x₂" title="Subscript" active={fmt.script === "sub"} onClick={() => setFormat("script", fmt.script === "sub" ? false : "sub")} />
            <Dropdown width={130} open={openMenu === "effects"} setOpen={o => setOpenMenu(o ? "effects" : null)}
              trigger={<RBtn small icon="S✦" title="Text Effects" onClick={() => setOpenMenu(m => m === "effects" ? null : "effects")} />}>
              <MenuItem onClick={() => { setFormat("texteffect", false); setOpenMenu(null); }}>None</MenuItem>
              {TEXT_EFFECTS.map(e => <MenuItem key={e.value} onClick={() => { setFormat("texteffect", e.value); setOpenMenu(null); }}>{e.label}</MenuItem>)}
            </Dropdown>
            <Dropdown width={180} open={openMenu === "highlight"} setOpen={o => setOpenMenu(o ? "highlight" : null)}
              trigger={<RBtn small icon={<span style={{ background: fmt.background || "#FFFF00", padding: "0 2px", borderRadius: 2 }}>A</span>} title="Text Highlight Color" onClick={() => setOpenMenu(m => m === "highlight" ? null : "highlight")} />}>
              <ColorSwatchGrid colors={HIGHLIGHT_COLORS} clearLabel="No Color" onPick={c => { setFormat("background", c); setOpenMenu(null); }} onClear={() => { setFormat("background", false); setOpenMenu(null); }} />
            </Dropdown>
            <Dropdown width={244} open={openMenu === "fontcolor"} setOpen={o => setOpenMenu(o ? "fontcolor" : null)}
              trigger={<RBtn small icon={<span style={{ borderBottom: "2.5px solid " + (fmt.color || "#A4262C"), padding: "0 1px" }}>A</span>} title="Font Color" onClick={() => setOpenMenu(m => m === "fontcolor" ? null : "fontcolor")} />}>
              <WordColorPicker clearLabel="Automatic" onPick={c => { setFormat("color", c); setOpenMenu(null); }} onClear={() => { setFormat("color", false); setOpenMenu(null); }} />
            </Dropdown>
          </div>
        </div>
      </RGroup>

      <RGroup label="Paragraph" style={{ minWidth: 240 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <div style={{ display: "flex", gap: 2 }}>
            {/* Bullet Library / Numbering Library — real per-glyph/per-format
                galleries (see BULLET_STYLES/NUMBER_FORMATS in wordFormats.js),
                matching Word's own Home-ribbon dropdowns rather than a single
                fixed bullet character. Clicking the icon opens the gallery
                (same pattern this file already uses for Change Case/Text
                Effects); the RBtn itself still shows "active" whenever any
                bullet/number list is applied, whichever glyph/format it is. */}
            <Dropdown width={168} open={openMenu === "bullets"} setOpen={o => setOpenMenu(o ? "bullets" : null)}
              trigger={<RBtn small icon="•≡" title="Bullets" active={fmt.list === "bullet"} onClick={() => setOpenMenu(m => m === "bullets" ? null : "bullets")} />}>
              <div style={{ fontSize: 9.5, color: LABEL_COLOR, textTransform: "uppercase", letterSpacing: ".3px", padding: "2px 6px 4px" }}>Bullet Library</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 4, padding: "0 6px 6px" }}>
                <button type="button" title="None" onMouseDown={e => e.preventDefault()} onClick={() => { setFormat("list", false); setOpenMenu(null); }}
                  style={{ height: 30, border: "1px solid " + (!fmt.list ? "#0078D4" : "#d1cec9"), borderRadius: 6, background: !fmt.list ? "#DEECF9" : "#fff", cursor: "pointer", fontSize: 10, color: LABEL_COLOR }}>None</button>
                {BULLET_STYLES.map(b => (
                  <button key={b.id} type="button" title={b.label} onMouseDown={e => e.preventDefault()}
                    onClick={() => { withSel(quill => { quill.format("list", "bullet", "user"); quill.format("bulletstyle", b.id, "user"); }); setOpenMenu(null); }}
                    style={{ height: 30, border: "1px solid " + (fmt.list === "bullet" && fmt.bulletstyle === b.id ? "#0078D4" : "#d1cec9"), borderRadius: 6, background: fmt.list === "bullet" && fmt.bulletstyle === b.id ? "#DEECF9" : "#fff", cursor: "pointer", fontSize: 16, color: LABEL_COLOR }}>
                    {b.glyph}
                  </button>
                ))}
              </div>
            </Dropdown>
            <Dropdown width={150} open={openMenu === "numbering"} setOpen={o => setOpenMenu(o ? "numbering" : null)}
              trigger={<RBtn small icon="1≡" title="Numbering" active={fmt.list === "ordered"} onClick={() => setOpenMenu(m => m === "numbering" ? null : "numbering")} />}>
              <div style={{ fontSize: 9.5, color: LABEL_COLOR, textTransform: "uppercase", letterSpacing: ".3px", padding: "2px 6px 4px" }}>Numbering Library</div>
              <MenuItem onClick={() => { setFormat("list", false); setOpenMenu(null); }}>None</MenuItem>
              {NUMBER_FORMATS.map(n => (
                <MenuItem key={n.id} onClick={() => { withSel(quill => { quill.format("list", "ordered", "user"); quill.format("numstyle", n.id, "user"); }); setOpenMenu(null); }}>
                  {n.label}
                </MenuItem>
              ))}
            </Dropdown>
            <RBtn small icon="1a≡" title="Multilevel List — Tab/Shift+Tab to change level" onClick={() => setFormat("list", "ordered")} />
            <RBtn small icon="⇤" title="Decrease Indent" onClick={() => withSel(quill => quill.format("indent", "-1", "user"))} />
            <RBtn small icon="⇥" title="Increase Indent" onClick={() => withSel(quill => quill.format("indent", "+1", "user"))} />
            <Dropdown width={210} open={openMenu === "spacing"} setOpen={o => setOpenMenu(o ? "spacing" : null)}
              trigger={<RBtn small icon="⇕≡" title="Line and Paragraph Spacing" onClick={() => setOpenMenu(m => m === "spacing" ? null : "spacing")} />}>
              {LINE_SPACINGS.map(s => <MenuItem key={s.v} onClick={() => { withSel((quill, sel) => quill.formatLine(sel.index, Math.max(sel.length, 1), "lineheight", s.v, "user")); setOpenMenu(null); }}>{s.l}</MenuItem>)}
              <div style={{ height: 1, background: "#e2e6f0", margin: "4px 2px" }} />
              <MenuItem onClick={() => { withSel((quill, sel) => quill.formatLine(sel.index, Math.max(sel.length, 1), "parabefore", fmt.parabefore ? false : "12pt", "user")); setOpenMenu(null); }}>
                {fmt.parabefore ? "Remove Space Before Paragraph" : "Add Space Before Paragraph"}
              </MenuItem>
              <MenuItem onClick={() => { withSel((quill, sel) => quill.formatLine(sel.index, Math.max(sel.length, 1), "paraafter", fmt.paraafter ? false : "12pt", "user")); setOpenMenu(null); }}>
                {fmt.paraafter ? "Remove Space After Paragraph" : "Add Space After Paragraph"}
              </MenuItem>
            </Dropdown>
            <RBtn small icon="A→Z" title="Sort" onClick={() => withSel(quill => sortSelectionLines(quill))} />
            <RBtn small icon="¶" title="Show/Hide Formatting Marks" active={formatMarksOn} onClick={onToggleFormatMarks} />
          </div>
          <div style={{ display: "flex", gap: 2 }}>
            <RBtn small icon={<RIcon name="dash" size={13} />} title="Align Left" active={!fmt.align} onClick={() => setFormat("align", false)} />
            <RBtn small icon="≡" title="Center" active={fmt.align === "center"} onClick={() => setFormat("align", "center")} />
            <RBtn small icon="≡→" title="Align Right" active={fmt.align === "right"} onClick={() => setFormat("align", "right")} />
            <RBtn small icon="☰" title="Justify" active={fmt.align === "justify"} onClick={() => setFormat("align", "justify")} />
            <Dropdown width={140} open={openMenu === "border"} setOpen={o => setOpenMenu(o ? "border" : null)}
              trigger={<RBtn small icon="▦" title="Borders" onClick={() => setOpenMenu(m => m === "border" ? null : "border")} />}>
              <MenuItem onClick={() => { withSel((quill, sel) => quill.formatLine(sel.index, Math.max(sel.length, 1), "pborder", false, "user")); setOpenMenu(null); }}>No Border</MenuItem>
              <MenuItem onClick={() => { withSel((quill, sel) => quill.formatLine(sel.index, Math.max(sel.length, 1), "pborder", "1px solid #605E5C", "user")); setOpenMenu(null); }}>Box Border</MenuItem>
              <MenuItem onClick={() => { withSel((quill, sel) => quill.formatLine(sel.index, Math.max(sel.length, 1), "pborder", "1px solid #605E5C", "user")); setOpenMenu(null); }}>Bottom Border</MenuItem>
            </Dropdown>
            <Dropdown width={244} open={openMenu === "shading"} setOpen={o => setOpenMenu(o ? "shading" : null)}
              trigger={<RBtn small icon="▨" title="Shading" onClick={() => setOpenMenu(m => m === "shading" ? null : "shading")} />}>
              <WordColorPicker clearLabel="No Color" onPick={c => { withSel((quill, sel) => quill.formatLine(sel.index, Math.max(sel.length, 1), "pshading", c, "user")); setOpenMenu(null); }} onClear={() => { withSel((quill, sel) => quill.formatLine(sel.index, Math.max(sel.length, 1), "pshading", false, "user")); setOpenMenu(null); }} />
            </Dropdown>
            {/* Word/Outlook's Insert > Horizontal Line — same divider blot,
                same result, as typing "---" then Enter (see RichTextEditor.js's
                auto-divider handler); this is the manual, click-anywhere path. */}
            <RBtn small icon="―" title="Horizontal Line — insert a divider at the cursor" onClick={() => insertHorizontalLine(q())} />
          </div>
        </div>
      </RGroup>

      {showStyles && (
        <RGroup label="Styles" style={{ minWidth: 220, maxWidth: 220 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            {STYLE_GALLERY.map(s => (
              <button key={s.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => withSel(quill => applyStyleGallery(quill, s.id))}
                title={s.label}
                style={{ flexShrink: 0, fontSize: 10, padding: "4px 7px", border: "1px solid #d1cec9", borderRadius: 6, background: "#fff", cursor: "pointer", color: LABEL_COLOR, whiteSpace: "nowrap" }}
                onMouseEnter={e => e.currentTarget.style.background = "#F0F4FF"} onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
                {s.label}
              </button>
            ))}
          </div>
        </RGroup>
      )}

      {onFindReplace && (
        <RGroup label="Editing">
          <RBtn small icon={<RIcon name="search" size={13} />} title="Find & Replace (Ctrl+H)" onClick={onFindReplace} />
        </RGroup>
      )}

      {/* New, additive group — Word (.docx) export/import. Self-contained:
          works off `getQuill` alone, so it appears automatically everywhere
          WordRibbon is already used (Workspace, Mail Merge, Sequence) with
          no changes required at any call site beyond optionally passing
          `addToast`/`docFilename`. */}
      <RGroup label="Word Doc" style={{ borderRight: "none" }}>
        <input ref={importInputRef} type="file" accept=".docx" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ""; if (f) importDocxFile(f); }} />
        <RBtn small icon={<RIcon name="download" size={13} />} title="Export as Word (.docx)" disabled={docBusy === "export"} onClick={exportDocx} />
        <RBtn small icon={<RIcon name="upload" size={13} />} title="Import Word Document (.docx) — inserts at cursor" disabled={docBusy === "import"} onClick={() => importInputRef.current?.click()} />
        <RBtn small icon={<RIcon name="print" size={13} />} title="Print / Save as PDF" onClick={printAsPdf} />
        <RBtn small icon={<RIcon name="textLines" size={13} />} title="Save as HTML" onClick={saveAsHtml} />
        <RBtn small icon={<RIcon name="grid" size={13} />} title="Command Palette (Ctrl+K)" onClick={() => setShowPalette(true)} />
      </RGroup>

      <CommandPalette open={showPalette} onClose={() => setShowPalette(false)} commands={commands} />
    </div>
  );
}

// Ctrl+K command palette — search-as-you-type over the command list above,
// arrow keys to move, Enter to run, Escape to close. Portaled to
// document.body like the file's other flyouts (see useFlyoutPosition)
// so it isn't clipped by any ribbon container's overflow.
function CommandPalette({ open, onClose, commands }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setQuery(""); setActiveIdx(0); } }, [open]);
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(c => c.label.toLowerCase().includes(q) || (c.keywords || "").includes(q));
  }, [query, commands]);

  if (!open) return null;

  const run = (cmd) => { if (!cmd) return; onClose(); cmd.run(); };

  return createPortal(
    // z-index must clear App.js's own top-level route wrappers, which use
    // 99999 for the full-screen Workspace/Mail Merge containers (the
    // highest value anywhere else in the app is 100001) — 10000 was
    // invisible underneath those on the two surfaces that use them.
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.25)", zIndex: 1000000, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "12vh" }}
      onClick={onClose}>
      <div style={{ width: 480, maxHeight: "60vh", background: "#fff", borderRadius: 10, boxShadow: "0 16px 48px rgba(16,24,40,.28)", overflow: "hidden", display: "flex", flexDirection: "column" }}
        onClick={e => e.stopPropagation()}>
        <input
          ref={inputRef}
          value={query}
          onChange={e => { setQuery(e.target.value); setActiveIdx(0); }}
          onKeyDown={e => {
            if (e.key === "Escape") { e.preventDefault(); onClose(); }
            else if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); run(filtered[activeIdx]); }
          }}
          placeholder="Type a command…"
          style={{ border: "none", borderBottom: "1px solid #e2e6f0", padding: "14px 16px", fontSize: 14, outline: "none", fontFamily: "'Segoe UI',sans-serif" }}
        />
        <div style={{ overflowY: "auto", padding: 6 }}>
          {filtered.length === 0 && <div style={{ padding: "14px 12px", fontSize: 13, color: "#8890B0" }}>No matching commands</div>}
          {filtered.map((c, i) => (
            <div key={c.id} onMouseEnter={() => setActiveIdx(i)} onClick={() => run(c)}
              style={{ padding: "9px 12px", borderRadius: 6, fontSize: 13, cursor: "pointer", background: i === activeIdx ? "#DEECF9" : "transparent", color: LABEL_COLOR }}>
              {c.label}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
